import React, { useEffect, useState, useMemo } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import qs from 'query-string';
import useAnalytics from 'helpers/useAnalytics';
import { useLocation, Prompt } from 'react-router';
import { useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Sift from '@sift/types';
import * as Types from 'pages/AttributesEdit/typings';
import { attributeTypes } from './AttributeTypeCard';

import Page from 'components/Page/newComponents';
import Loader from 'components/Loader';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import AttributeBasicInfo from 'pages/AttributesEdit/components/AttributeBasicInfo';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { USER_URL } from 'components/Router/consts';

interface Props {
  newAttributeResponse: Sift.Field;
  onClearFetches: () => void;
  onRedirect: () => void;
  onSubmit: (values: Types.AttributesEditFormValues) => void;
  onUpdateParentField: () => void;
}

function AttributesCreateNew({
  newAttributeResponse,
  onClearFetches,
  onRedirect,
  onSubmit,
  onUpdateParentField,
}: Props) {
  const classes = useStyles();
  const location = useLocation();
  const analytics = useAnalytics();
  const query = useMemo(() => {
    return qs.parse(location.search);
  }, [location.search]);
  let { entityTypeKey, baseField } = useParams<{
    entityTypeKey: string;
    baseField: string;
  }>();
  const parentObjectKey = _get(query, 'parentObjectKey');
  const subAttribute = Boolean(parentObjectKey) && Boolean(entityTypeKey);
  const item = _get(attributeTypes, `${baseField}`);
  const initialState = {
    client: {
      displayName: '',
      display: { description: '' },
    },
  };

  useEffect(() => {
    document.title = 'New Attribute';
  }, []);

  useEffect(() => {
    if (subAttribute && newAttributeResponse) {
      onUpdateParentField();
    } else if (newAttributeResponse) {
      onRedirect();
    }

    return () => {
      onClearFetches();
    };
  }, [newAttributeResponse]);

  return (
    <Page classes={{ root: classes.pageRoot }}>
      {subAttribute && (
        <div className={classes.subAttribute}>
          <Typography variant="caption">
            This will be a sub-attribute of "Reach out to me for" attribute
          </Typography>
        </div>
      )}
      <Formik initialValues={initialState} enableReinitialize={true} onSubmit={onSubmit}>
        {props => {
          const { values, setFieldValue, dirty, resetForm } = props;
          const errors = props.errors as Types.AttributesFormErrors;
          const handleCreateNewAttribute = () => {
            props.handleSubmit();
            if (location.pathname.includes(USER_URL)) {
              analytics.track('New Attribute created', {
                category: 'AD - Person Profiles',
              });
            } else {
              analytics.track('New Attribute created', {
                category: 'AD - Pages',
              });
            }
          };
          return (
            <Form>
              <AttributeBasicInfo item={item} values={values.client} onChange={setFieldValue} />
              <PageFormToolbar dirty={dirty} preContent={<PageFormErrorMessage errors={errors} />}>
                <CancelButton onClick={resetForm} />
                <SaveButton
                  label="Save and Next"
                  onClick={() => {
                    handleCreateNewAttribute();
                    resetForm({ values });
                  }}
                  disabled={!dirty || !_isEmpty(errors)}
                />
              </PageFormToolbar>
              <Prompt
                when={dirty}
                message="Your work is not saved! Are you sure you want to leave?"
              />
            </Form>
          );
        }}
      </Formik>
    </Page>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    width: '100%',
  },
  pageRoot: {
    display: 'flex',
    flex: '1 1 auto',
    width: '100vw',
  },
  subAttribute: {
    background: 'rgba(41, 98, 255, 0.04) !important',
    border: `1px solid rgba(41, 98, 255, 0.12)`,
    borderRadius: theme.spacing(0.75),
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(3),
    padding: theme.spacing(0.5, 1),
    width: 'fit-content',
  },
}));

export default AttributesCreateNew;
